import React, { useState, useEffect, useRef } from 'react';
import { Box, Flex, VStack, useToast } from '@chakra-ui/react';
import { Header, Heading } from '@/layout/Header';
import { useGenAIResponse } from './hooks/useGenAIResponse';
import ChatMessage from './components/ChatMessage';
import ChatInput from './components/ChatInput';
import WelcomeScreen from './components/WelcomeScreen';
import { Message, StoredChat } from './types';
import { STORAGE_KEY, TTL_HOURS, SAMPLE_PROMPTS } from './constants';

const GenAIChatbot = (): JSX.Element => {
  const [messages, setMessages] = useState<Message[]>(() => {
    // Initialize messages from localStorage if available
    const savedChat = localStorage.getItem(STORAGE_KEY);
    if (savedChat) {
      try {
        const parsedData = JSON.parse(savedChat);
        // Ensure we have the correct data structure
        if (parsedData && Array.isArray(parsedData.messages)) {
          const { messages: savedMessages, timestamp } = parsedData as StoredChat;
          const now = Date.now();
          const ttlMs = TTL_HOURS * 60 * 60 * 1000;

          // Check if messages are within TTL
          if (now - timestamp < ttlMs) {
            return savedMessages;
          }
        }
        // If data is invalid or expired, clear it
        localStorage.removeItem(STORAGE_KEY);
        return [];
      } catch (error) {
        // If there's any error parsing the stored data, clear it
        localStorage.removeItem(STORAGE_KEY);
        return [];
      }
    }
    return [];
  });
  const [input, setInput] = useState('');
  const toast = useToast();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = (): void => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    // Ensure messages is an array before saving
    if (Array.isArray(messages)) {
      // Filter out loading messages before saving
      const messagesToSave = messages.filter(msg => !msg.isLoading);
      const chatData: StoredChat = {
        messages: messagesToSave,
        timestamp: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(chatData));
    }
  }, [messages]);

  // Clear messages on logout
  useEffect(() => {
    const handleLogout = (): void => {
      localStorage.removeItem(STORAGE_KEY);
      setMessages([]);
    };

    // Listen for logout event
    window.addEventListener('logout', handleLogout);

    return () => {
      window.removeEventListener('logout', handleLogout);
    };
  }, []);

  const { mutate: getGenAIResponse, isLoading } = useGenAIResponse({
    onSuccess: (response): void => {
      // Remove loading message and add actual response
      setMessages(prev => {
        const withoutLoading = prev.filter(msg => !msg.isLoading);
        return [...withoutLoading, { type: 'bot', content: response || "I couldn't process that request." }];
      });
    },
    onError: (error): void => {
      toast({
        title: 'Error',
        description: 'Failed to get response. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });

      // Remove loading message and add error message
      setMessages(prev => {
        const withoutLoading = prev.filter(msg => !msg.isLoading);
        return [...withoutLoading, { type: 'bot', content: 'Sorry, I encountered an error. Please try again.' }];
      });
    },
  });

  const handleSendMessage = async (message?: string): Promise<void> => {
    const messageToSend = message || input.trim();
    if (!messageToSend || isLoading) return;

    setInput('');

    // Ensure messages is an array before updating
    setMessages(prev => {
      const currentMessages = Array.isArray(prev) ? prev : [];
      const timestamp = Date.now();
      return [
        ...currentMessages,
        {
          type: 'user',
          content: messageToSend,
          id: `user-${timestamp}`,
        },
        {
          type: 'bot',
          content: '',
          isLoading: true,
          id: `bot-loading-${timestamp}`,
        },
      ];
    });

    // Call the service
    getGenAIResponse({ userQuery: messageToSend });
  };

  const handleKeyPress = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Box h="100vh" bg="white.100" w="full">
      <Header>
        <Heading>
          <Heading.Title>Bureau AI Chatbot</Heading.Title>
        </Heading>
      </Header>

      <Box h="calc(100vh - 80px)" py={4} px={4} bg="white.100" w="full">
        <Flex direction="column" h="full" maxW="1200px" mx="auto">
          {messages.length === 0 ? (
            <WelcomeScreen
              input={input}
              setInput={setInput}
              handleSendMessage={handleSendMessage}
              isLoading={isLoading}
              onKeyPress={handleKeyPress}
              samplePrompts={SAMPLE_PROMPTS}
            />
          ) : (
            <>
              <VStack
                flex={1}
                w="full"
                spacing={4}
                overflowY="auto"
                px={2}
                py={4}
                sx={{
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'gray.100',
                    borderRadius: 'full',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'gray.300',
                    borderRadius: 'full',
                    '&:hover': {
                      background: 'gray.400',
                    },
                  },
                }}
              >
                {messages.map(message => (
                  <ChatMessage key={message.id || message.content} message={message} />
                ))}
                <div ref={messagesEndRef} />
              </VStack>
              <ChatInput
                input={input}
                setInput={setInput}
                handleSendMessage={() => handleSendMessage()}
                isLoading={isLoading}
                onKeyPress={handleKeyPress}
              />
            </>
          )}
        </Flex>
      </Box>
    </Box>
  );
};

export default GenAIChatbot;
